import { RowDataPacket } from 'mysql2';
import { parseJsonValue } from '@/lib/json';

export type HomepageSlide = {
  img: string;
  href: string;
  title: string;
  subtitle: string;
  enabled: boolean;
  sort_order: number;
};

export type HomepageBanner = {
  img: string;
  href: string;
  alt: string;
  enabled: boolean;
  sort_order: number;
};export type HomepageFlashTab = {
  label: string;
  href: string;
  matchKeywords?: string[];
  matchTags?: string[];
  skuPrefixes?: string[];
  subcategorySlugs?: string[];
  productSlugs?: string[];
};

export type HomepageFlashSection = {
  title: string;
  tabs: HomepageFlashTab[];
  viewAllHref: string;
  enabled: boolean;
  sort_order: number;
};

export type HomepageCategoryItem = {
  name: string;
  href: string;
  img: string;
  enabled: boolean;
  sort_order: number;
};

export type HomepagePartnerItem = {
  src: string;
  alt: string;
  href: string;
  enabled: boolean;
  sort_order: number;
};

export type HomepageShowroomItem = {
  number: string;
  name: string;
  address: string;
  image?: string;
  mapHref: string;
  phone: string;
  warranty: string;
  enabled: boolean;
  sort_order: number;
};

export type HomepagePolicyItem = {
  icon: string;
  title: string;
  desc: string;
  enabled: boolean;
  sort_order: number;
};

export type HomepageFloatingButton = {
  enabled: boolean;
  label: string;
  sublabel?: string;
  href: string;
  bgColor: string;
  textColor: string;
  type: 'hotline' | 'zalo' | 'messenger';
};

export type HomepagePcBuilderSideBannerItem = {
  img: string;
  href: string;
  alt: string;
  enabled: boolean;
  verticalAlign: 'top' | 'center' | 'bottom';
  offsetX: number;
  offsetY: number;
  imagePosX: number;
  imagePosY: number;
};

export type HomepagePcBuilderSideBanners = {
  enabled: boolean;
  width: number;
  height: number;
  left: HomepagePcBuilderSideBannerItem;
  right: HomepagePcBuilderSideBannerItem;
};

export type HomepageFooterLink = {
  label: string;
  href: string;
};

export type HomepageFooterGroup = {
  title: string;
  links: HomepageFooterLink[];
};

export type HomepageFooterSocial = {
  icon: string;
  label: string;
  href: string;
};

export type HomepageConfig = {
  sectionVisibility: {
    heroSlider: boolean;  flashSections: boolean;
    categoryGrid: boolean;
    partnerSection: boolean;
    showroomSection: boolean;
    policySection: boolean;
    floatingButtons: boolean;
    footerSection: boolean;
  };
  heroSlides: HomepageSlide[]; flashSections: HomepageFlashSection[];
  categoryGrid: {
    /** Nhãn nhỏ phía trên (copy UI — danh mục lấy từ DB) */
    eyebrow: string;
    title: string;
    sectionSubtitle: string;
    /** Dự phòng khi chưa có danh mục cấp 1 trong DB */
    items: HomepageCategoryItem[];
  };
  partnerSection: {
    title: string;
    items: HomepagePartnerItem[];
  };  showroomSection: {
    title: string;
    items: HomepageShowroomItem[];
  };
  policySection: {
    items: HomepagePolicyItem[];
  };
  floatingButtons: HomepageFloatingButton[];
  pcBuilderSideBanners: HomepagePcBuilderSideBanners;
  footerSection: {
    groups: HomepageFooterGroup[];
    socials: HomepageFooterSocial[];
    bottomText: string;
  };
};

function normalizeSupportLinkText(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .trim();
}

function normalizeFooterSupportLinks(groups: HomepageFooterGroup[]): HomepageFooterGroup[] {
  return (groups || []).map((group) => ({
    ...group,
    links: (group.links || [])
      .map((link) => {
        const label = String(link?.label || '').trim();
        const isLegacyCskh = normalizeSupportLinkText(label).startsWith('cskh:');
        if (!isLegacyCskh) return link;
        return { label: 'Hỗ trợ nhanh', href: '#chat-quick' };
      })
      .filter(
        (link) => !normalizeSupportLinkText(String(link?.label || '')).includes('lap dat phong net')
      ),
  }));
}

function normalizeFooterSocialLinks(socials: HomepageFooterSocial[]): HomepageFooterSocial[] {
  return (socials || []).map((social) => {
    const icon = String(social.icon || '')
      .toLowerCase()
      .trim();
    if (icon === 'zalo') {
      return { ...social, href: 'https://zalo.me/0337960997' };
    }
    if (icon === 'facebook' || icon === 'fb') {
      return {
        ...social,
        href: 'https://www.facebook.com/profile.php?id=61574523989673',
      };
    }
    return social;
  });
}

const RAM_FLASH_SECTION: HomepageFlashSection = {
  title: 'RAM',
  tabs: [
    {
      label: 'RAM DDR4',
      href: '/ram',
      subcategorySlugs: ['ram'],
      matchTags: ['subcategory:ram', 'category:ram'],
      matchKeywords: ['ram ddr4', 'ddr4', 'pc4'],
    },
    {
      label: 'RAM DDR5',
      href: '/ram',
      subcategorySlugs: ['ram'],
      matchTags: ['subcategory:ram', 'category:ram'],
      matchKeywords: ['ram ddr5', 'ddr5', 'pc5'],
    },
  ],
  viewAllHref: '/ram',
  enabled: true,
  sort_order: 4,
};

function createSingleTabFlashSection(input: {
  title: string;
  href: string;
  keywords: string[];
  tags: string[];
  sort_order: number;
}): HomepageFlashSection {
  return {
    title: input.title,
    tabs: [
      {
        label: input.title,
        href: input.href,
        matchKeywords: input.keywords,
        matchTags: input.tags,
      },
    ],
    viewAllHref: input.href,
    enabled: true,
    sort_order: input.sort_order,
  };
}

const REQUIRED_FLASH_SECTIONS: HomepageFlashSection[] = [
  RAM_FLASH_SECTION,
  createSingleTabFlashSection({
    title: 'LAPTOP',
    href: '/danh-muc/laptop',
    keywords: ['laptop', 'notebook', 'macbook'],
    tags: ['category:laptop', 'subcategory:laptop'],
    sort_order: 20,
  }),
  createSingleTabFlashSection({
    title: 'CASE',
    href: '/danh-muc/case-gaming',
    keywords: ['case', 'vo case', 'thung may'],
    tags: ['category:case', 'subcategory:case'],
    sort_order: 21,
  }),
  createSingleTabFlashSection({
    title: 'TẢN',
    href: '/danh-muc/tan-nhiet',
    keywords: ['tan nhiet', 'cpu cooler', 'aio', 'heatsink'],
    tags: ['category:tan-nhiet', 'subcategory:tan-nhiet', 'subcategory:cooling'],
    sort_order: 22,
  }),
  createSingleTabFlashSection({
    title: 'NGUỒN',
    href: '/danh-muc/nguon-may-tinh',
    keywords: ['psu', 'nguon', 'power supply'],
    tags: ['category:nguon-psu', 'subcategory:psu', 'subcategory:nguon-psu'],
    sort_order: 23,
  }),
  createSingleTabFlashSection({
    title: 'PHÍM',
    href: '/danh-muc/ban-phim',
    keywords: ['ban phim', 'keyboard', 'phim co'],
    tags: ['category:ban-phim', 'subcategory:ban-phim', 'subcategory:keyboard'],
    sort_order: 24,
  }),
  createSingleTabFlashSection({
    title: 'CHUỘT',
    href: '/danh-muc/chuot',
    keywords: ['chuot', 'mouse'],
    tags: ['category:chuot', 'subcategory:chuot', 'subcategory:mouse'],
    sort_order: 25,
  }),
  createSingleTabFlashSection({
    title: 'TAI NGHE',
    href: '/danh-muc/tai-nghe',
    keywords: ['tai nghe', 'headphone', 'earphone'],
    tags: ['category:tai-nghe', 'subcategory:tai-nghe', 'subcategory:headset'],
    sort_order: 26,
  }),
  createSingleTabFlashSection({
    title: 'THIẾT BỊ MẠNG',
    href: '/danh-muc/thiet-bi-mang',
    keywords: ['thiet bi mang', 'router', 'switch', 'wifi'],
    tags: ['category:thiet-bi-mang', 'subcategory:thiet-bi-mang', 'subcategory:networking'],
    sort_order: 27,
  }),
  createSingleTabFlashSection({
    title: 'MÁY IN,UPS',
    href: '/danh-muc/may-in-ups',
    keywords: ['may in', 'printer', 'ups', 'bo luu dien'],
    tags: ['category:may-in-ups', 'subcategory:printer', 'subcategory:ups'],
    sort_order: 28,
  }),
  createSingleTabFlashSection({
    title: 'CAMERA',
    href: '/danh-muc/camera-trong-nha',
    keywords: ['camera'],
    tags: ['category:camera', 'subcategory:camera'],
    sort_order: 29,
  }),
  createSingleTabFlashSection({
    title: 'WEBCAM',
    href: '/danh-muc/webcam',
    keywords: ['webcam'],
    tags: ['category:webcam', 'subcategory:webcam'],
    sort_order: 30,
  }),
];

function withRequiredFlashSections(sections: HomepageFlashSection[]): HomepageFlashSection[] {
  const list = Array.isArray(sections) ? [...sections] : [];
  const normalizedTitles = new Set(
    list.map((section) =>
      String(section?.title || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
    )
  );

  REQUIRED_FLASH_SECTIONS.forEach((requiredSection) => {
    const key = String(requiredSection.title || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    if (normalizedTitles.has(key)) return;
    list.push(requiredSection);
    normalizedTitles.add(key);
  });

  return list;
}

const DEFAULT_CATEGORY_IMAGE_OVERRIDES: HomepageCategoryItem[] = [
  { name: 'CPU', href: '/danh-muc/cpu', img: '', enabled: true, sort_order: 1 },
  { name: 'Mainboard', href: '/danh-muc/mainboard', img: '', enabled: true, sort_order: 2 },
  { name: 'VGA', href: '/danh-muc/vga', img: '', enabled: true, sort_order: 3 },
  { name: 'Ram', href: '/danh-muc/ram', img: '', enabled: true, sort_order: 4 },
  { name: 'SSD', href: '/danh-muc/ssd-nvme', img: '', enabled: true, sort_order: 5 },
  { name: 'HDD', href: '/danh-muc/hdd', img: '', enabled: true, sort_order: 6 },
  { name: 'Laptop', href: '/danh-muc/laptop', img: '', enabled: true, sort_order: 7 },
  { name: 'Màn hình', href: '/danh-muc/man-hinh', img: '', enabled: true, sort_order: 8 },
  { name: 'Case', href: '/danh-muc/case-gaming', img: '', enabled: true, sort_order: 9 },
  { name: 'Tản nhiệt', href: '/danh-muc/tan-nhiet', img: '', enabled: true, sort_order: 10 },
  {
    name: 'Nguồn máy tính',
    href: '/danh-muc/nguon-may-tinh',
    img: '',
    enabled: true,
    sort_order: 11,
  },
  { name: 'Bàn phím', href: '/danh-muc/ban-phim', img: '', enabled: true, sort_order: 12 },
  { name: 'Chuột', href: '/danh-muc/chuot', img: '', enabled: true, sort_order: 13 },
  { name: 'Tai nghe', href: '/danh-muc/tai-nghe', img: '', enabled: true, sort_order: 14 },
  {
    name: 'Thiết bị mạng',
    href: '/danh-muc/thiet-bi-mang',
    img: '',
    enabled: true,
    sort_order: 15,
  },
  { name: 'Máy in, UPS', href: '/danh-muc/may-in-ups', img: '', enabled: true, sort_order: 16 },
  { name: 'Camera', href: '/danh-muc/camera-trong-nha', img: '', enabled: true, sort_order: 17 },
  { name: 'Webcam', href: '/danh-muc/webcam', img: '', enabled: true, sort_order: 18 },
];

function mergeCategoryImageOverrides(items: HomepageCategoryItem[]): HomepageCategoryItem[] {
  const existing = Array.isArray(items) ? items : [];
  const usedIndexes = new Set<number>();

  const normalize = (value: string) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .trim();

  const matches = (a: HomepageCategoryItem, b: HomepageCategoryItem) => {
    const aName = normalize(a.name);
    const bName = normalize(b.name);
    const aHref = normalize(a.href);
    const bHref = normalize(b.href);
    return (
      (aHref && bHref && aHref === bHref) ||
      (aName && bName && (aName === bName || aName.includes(bName) || bName.includes(aName)))
    );
  };

  const mergedDefaults = DEFAULT_CATEGORY_IMAGE_OVERRIDES.map((base) => {
    const matchIndex = existing.findIndex(
      (item, index) => !usedIndexes.has(index) && matches(item, base)
    );
    if (matchIndex < 0) return base;
    usedIndexes.add(matchIndex);
    const match = existing[matchIndex];
    return {
      ...base,
      img: match.img || base.img,
      enabled: match.enabled !== false,
      sort_order: Number(match.sort_order || base.sort_order),
    };
  });

  const customItems = existing.filter((_, index) => !usedIndexes.has(index));
  return [...mergedDefaults, ...customItems].sort(
    (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
  );
}

export const defaultHomepageConfig: HomepageConfig = {
  sectionVisibility: {
    heroSlider: true,  flashSections: true,
    categoryGrid: true,
    partnerSection: true,
    showroomSection: true,
    policySection: true,
    floatingButtons: true,
    footerSection: true,
  },
  heroSlides: [
    {
      img: '/assets/brand/dntech-home-hero-performance-v2.png',
      href: '#',
      title: 'GIÁ THẬT SỐC THẬT',
      subtitle: 'Ưu đãi đến 65%++, giá sốc chỉ từ 69.000đ!!!',
      enabled: true,
      sort_order: 1,
    },
    {
      img: '/assets/brand/dntech-home-hero-creator-v2.png',
      href: '#',
      title: 'NÂNG CẤP BẬT VỊ THẾ',
      subtitle: 'Combo tiết kiệm đến 5 triệu!',
      enabled: true,
      sort_order: 2,
    },
    {
      img: '/assets/brand/dntech-home-hero-cpu-v3.png',
      href: '#',
      title: 'CPU mạnh. Build PC chuẩn.',
      subtitle: 'Chọn đúng vi xử lý, cân bằng hiệu năng và ngân sách.',
      enabled: true,
      sort_order: 3,
    },
    {
      img: '/assets/brand/dntech-home-hero-ram-v3.png',
      href: '#',
      title: 'RAM nhanh. Đa nhiệm mượt.',
      subtitle: 'Nâng cấp bộ nhớ, tối ưu tốc độ cho game và công việc.',
      enabled: true,
      sort_order: 4,
    },
  ], flashSections: [
    {
      title: 'CPU',
      tabs: [
        {
          label: 'CPU Intel',
          href: '/cpu',
          subcategorySlugs: ['cpu'],
          matchTags: ['subcategory:cpu', 'category:cpu', 'brand:intel'],
          matchKeywords: ['cpu intel', 'intel core', 'core i'],
        },
        {
          label: 'CPU AMD',
          href: '/cpu',
          subcategorySlugs: ['cpu'],
          matchTags: ['subcategory:cpu', 'category:cpu', 'brand:amd'],
          matchKeywords: ['cpu amd', 'ryzen', 'amd', 'bộ vi xử lý amd', 'bo vi xu ly amd'],
        },
      ],
      viewAllHref: '/cpu',
      enabled: true,
      sort_order: 1,
    },
    {
      title: 'Mainboard',
      tabs: [
        {
          label: 'Mainboard Intel',
          href: '/mainboard',
          subcategorySlugs: ['mainboard'],
          matchTags: ['subcategory:mainboard', 'category:mainboard', 'brand:intel'],
          matchKeywords: ['mainboard intel', 'motherboard intel', 'b760', 'z790', 'h610'],
        },
        {
          label: 'Mainboard AMD',
          href: '/mainboard',
          subcategorySlugs: ['mainboard'],
          matchTags: ['subcategory:mainboard', 'category:mainboard', 'brand:amd'],
          matchKeywords: ['mainboard amd', 'amd', 'b650', 'x670', 'a620', 'b550', 'am4', 'am5'],
        },
      ],
      viewAllHref: '/mainboard',
      enabled: true,
      sort_order: 2,
    },
    {
      title: 'VGA',
      tabs: [
        {
          label: 'VGA NVIDIA',
          href: '/vga',
          subcategorySlugs: ['vga'],
          matchTags: ['subcategory:vga', 'category:vga', 'brand:nvidia', 'geforce', 'gpu'],
          matchKeywords: ['vga nvidia', 'nvidia', 'geforce', 'rtx', 'gtx'],
        },
        {
          label: 'VGA AMD',
          href: '/vga',
          subcategorySlugs: ['vga'],
          matchTags: ['subcategory:vga', 'category:vga', 'brand:amd', 'radeon', 'gpu'],
          matchKeywords: ['vga amd', 'radeon', 'rx', 'amd gpu'],
        },
      ],
      viewAllHref: '/vga',
      enabled: true,
      sort_order: 3,
    },
    RAM_FLASH_SECTION,
  ],
  categoryGrid: {
    eyebrow: 'DANH MỤC',
    title: 'Khám Phá Linh Kiện',
    sectionSubtitle: 'Từ RAM tốc độ cao đến GPU gaming — tất cả đều chính hãng',
    items: DEFAULT_CATEGORY_IMAGE_OVERRIDES,
  },
  partnerSection: {
    title: 'Đối tác chiến lược',
    items: [
      {
        src: '',
        alt: 'NVIDIA',
        href: '#',
        enabled: true,
        sort_order: 1,
      },
      {
        src: '',
        alt: 'Intel',
        href: '#',
        enabled: true,
        sort_order: 2,
      },
      {
        src: '',
        alt: 'AMD',
        href: '#',
        enabled: true,
        sort_order: 3,
      },
      { src: '', alt: 'ASUS', href: '#', enabled: true, sort_order: 4 },
      { src: '', alt: 'MSI', href: '#', enabled: true, sort_order: 5 },
      { src: '', alt: 'Gigabyte', href: '#', enabled: true, sort_order: 6 },
      { src: '', alt: 'Samsung', href: '#', enabled: true, sort_order: 7 },
      { src: '', alt: 'Kingston', href: '#', enabled: true, sort_order: 8 },
      { src: '', alt: 'Logitech', href: '#', enabled: true, sort_order: 9 },
      { src: '', alt: 'Corsair', href: '#', enabled: true, sort_order: 10 },
    ],
  },  showroomSection: {
    title: 'HỆ THỐNG SHOWROOM DN TECH',
    items: [
      {
        number: '1',
        name: '★ Showroom Tân Hoà (Tân Bình)',
        address: '28 - 30 Trần Triệu Luật, P.Tân Hoà, TP.HCM',
        image: '',
        mapHref: 'https://goo.gl/maps/tHWEPpKaAz3c2Uaf6',
        phone: 'Mua hàng: 0337960997 (08:30 - 21:00)',
        warranty: 'Bảo hành: 0337960997 (T2 - T7 | Sáng: 8:30 - 12h00; Chiều: 13h00 - 17:30)',
        enabled: true,
        sort_order: 1,
      },
      {
        number: '2',
        name: '★ Showroom Tân Hưng (Quận 7)',
        address: 'A9-10-11, Đường số 1, Khu dân cư Kim Sơn, P.Tân Hưng, TP.HCM',
        image: '',
        mapHref: 'https://maps.app.goo.gl/xCtcTXodVMXyT2cXA',
        phone: 'Mua hàng: 0337960997 (08:30 - 21:00)',
        warranty: 'Bảo hành: 0337960997 (T2 - T7 | Sáng: 9:00 - 12h00; Chiều: 13h00 - 17:30)',
        enabled: true,
        sort_order: 2,
      },
    ],
  },
  policySection: {
    items: [
      {
        icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_policy_page_1.png?v=6754',
        title: 'CHÍNH SÁCH GIAO HÀNG',
        desc: 'Nhận hàng và thanh toán tại nhà',
        enabled: true,
        sort_order: 1,
      },
      {
        icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_policy_page_2.png?v=6754',
        title: 'ĐỔI TRẢ DỄ DÀNG',
        desc: '1 đổi 1 trong 7 ngày',
        enabled: true,
        sort_order: 2,
      },
      {
        icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_policy_page_3.png?v=6754',
        title: 'GIÁ LUÔN HỢP LÝ NHẤT',
        desc: 'Giá cả hợp lý, nhiều ưu đãi tốt',
        enabled: true,
        sort_order: 3,
      },
      {
        icon: 'https://theme.hstatic.net/200000420363/1001333448/14/icon_policy_page_4.png?v=6754',
        title: 'HỔ TRỢ NHIỆT TÌNH',
        desc: 'Tư vấn, giải đáp mọi thắc mắc',
        enabled: true,
        sort_order: 4,
      },
    ],
  },
  floatingButtons: [
    {
      enabled: true,
      label: 'Tư vấn ngay',
      sublabel: '0337960997',
      href: 'tel:0337960997',
      bgColor: 'bg-primary',
      textColor: 'text-white',
      type: 'hotline',
    },
    {
      enabled: true,
      label: 'Zalo',
      href: 'https://zalo.me/0337960997',
      bgColor: 'bg-blue-500',
      textColor: 'text-white',
      type: 'zalo',
    },
    {
      enabled: true,
      label: 'Messenger',
      href: 'https://m.me/514958918520089',
      bgColor: 'bg-blue-600',
      textColor: 'text-white',
      type: 'messenger',
    },
  ],
  pcBuilderSideBanners: {
    enabled: false,
    width: 136,
    height: 339,
    left: {
      img: '/assets/brand/dntech-side-build-v2.png',
      href: '/xay-dung-cau-hinh',
      alt: 'PC gaming giá rẻ - Cấu hình tối ưu',
      enabled: true,
      verticalAlign: 'center',
      offsetX: 0,
      offsetY: 0,
      imagePosX: 50,
      imagePosY: 50,
    },
    right: {
      img: '/assets/brand/dntech-side-support-v2.png',
      href: '/tra-cuu-bao-hanh',
      alt: 'Bảo hành an tâm - Tra cứu nhanh',
      enabled: true,
      verticalAlign: 'center',
      offsetX: 0,
      offsetY: 0,
      imagePosX: 50,
      imagePosY: 50,
    },
  },
  footerSection: {
    groups: [
      {
        title: 'GIỚI THIỆU DN TECH',
        links: [
          { label: 'Về chúng tôi', href: '#' },
          { label: 'Tư vấn mua hàng', href: '#' },
          { label: 'Tuyển dụng', href: '#' },
        ],
      },
      {
        title: 'CHÍNH SÁCH CHUNG',
        links: [
          { label: 'Chính sách bảo mật', href: '#' },
          { label: 'Chính sách giao nhận, kiểm hàng', href: '#' },
          { label: 'Chính sách trả góp', href: '#' },
        ],
      },
      {
        title: 'THÔNG TIN KHUYẾN MÃI',
        links: [
          { label: 'Trang tin tức - tư vấn', href: '#' },
        ],
      },
      {
        title: 'HỖ TRỢ KHÁCH HÀNG',
        links: [
          { label: 'Hỗ trợ nhanh', href: '#chat-quick' },
          { label: 'Tổng hợp hotline, phản ánh.', href: '#' },
          { label: 'Tra cứu bảo hành', href: '#' },
        ],
      },
    ],
    socials: [
      {
        icon: 'facebook',
        label: 'Facebook',
        href: 'https://www.facebook.com/profile.php?id=61574523989673',
      },
      { icon: 'youtube', label: 'YouTube', href: 'https://www.youtube.com' },
      { icon: 'tiktok', label: 'TikTok', href: 'https://www.tiktok.com' },
      { icon: 'zalo', label: 'Zalo', href: 'https://zalo.me/0337960997' },
    ],
    bottomText:
      'Công ty TNHH DN TECH | MST: 0312152569 - 05/02/2013 - Sở kế hoạch và đầu tư TP.HCM | Trụ sở chính: 28 - 30 Trần Triệu Luật, P.6, Quận Tân Bình, TP.HCM | Hotline: 0337960997 | Email: info@dntech.vn',
  },
};

export function normalizeHomepageConfig(rawValue: unknown): HomepageConfig {
  const parsed = parseJsonValue<HomepageConfig>(rawValue, defaultHomepageConfig);
  const toFiniteNumber = (value: unknown, fallback: number) => {
    const parsedNumber = Number(value);
    return Number.isFinite(parsedNumber) ? parsedNumber : fallback;
  };

  return {
    sectionVisibility: {
      heroSlider: parsed.sectionVisibility?.heroSlider !== false,    flashSections: parsed.sectionVisibility?.flashSections !== false,
      categoryGrid: parsed.sectionVisibility?.categoryGrid !== false,
      partnerSection: parsed.sectionVisibility?.partnerSection !== false,
      showroomSection: parsed.sectionVisibility?.showroomSection !== false,
      policySection: parsed.sectionVisibility?.policySection !== false,
      floatingButtons: parsed.sectionVisibility?.floatingButtons !== false,
      footerSection: parsed.sectionVisibility?.footerSection !== false,
    },
    heroSlides: Array.isArray(parsed.heroSlides)
      ? parsed.heroSlides
      : defaultHomepageConfig.heroSlides,   flashSections: withRequiredFlashSections(
      Array.isArray(parsed.flashSections)
        ? parsed.flashSections
        : defaultHomepageConfig.flashSections
    ),
    categoryGrid: {
      eyebrow: parsed.categoryGrid?.eyebrow ?? defaultHomepageConfig.categoryGrid.eyebrow,
      title: parsed.categoryGrid?.title || defaultHomepageConfig.categoryGrid.title,
      sectionSubtitle:
        parsed.categoryGrid?.sectionSubtitle ?? defaultHomepageConfig.categoryGrid.sectionSubtitle,
      items: mergeCategoryImageOverrides(
        Array.isArray(parsed.categoryGrid?.items)
          ? parsed.categoryGrid.items
          : defaultHomepageConfig.categoryGrid.items
      ),
    },
    partnerSection: {
      title: parsed.partnerSection?.title || defaultHomepageConfig.partnerSection.title,
      items: Array.isArray(parsed.partnerSection?.items)
        ? parsed.partnerSection.items
        : defaultHomepageConfig.partnerSection.items,
    },    showroomSection: {
      title: parsed.showroomSection?.title || defaultHomepageConfig.showroomSection.title,
      items: Array.isArray(parsed.showroomSection?.items)
        ? parsed.showroomSection.items.map((item) => ({ ...item, image: item.image || '' }))
        : defaultHomepageConfig.showroomSection.items,
    },
    policySection: {
      items: Array.isArray(parsed.policySection?.items)
        ? parsed.policySection.items
        : defaultHomepageConfig.policySection.items,
    },
    floatingButtons: Array.isArray(parsed.floatingButtons)
      ? parsed.floatingButtons
      : defaultHomepageConfig.floatingButtons,
    pcBuilderSideBanners: {
      enabled: parsed.pcBuilderSideBanners?.enabled !== false,
      width:
        Number(parsed.pcBuilderSideBanners?.width) > 0
          ? toFiniteNumber(
              parsed.pcBuilderSideBanners?.width,
              defaultHomepageConfig.pcBuilderSideBanners.width
            )
          : defaultHomepageConfig.pcBuilderSideBanners.width,
      height:
        Number(parsed.pcBuilderSideBanners?.height) > 0
          ? toFiniteNumber(
              parsed.pcBuilderSideBanners?.height,
              defaultHomepageConfig.pcBuilderSideBanners.height
            )
          : defaultHomepageConfig.pcBuilderSideBanners.height,
      left: {
        img:
          parsed.pcBuilderSideBanners?.left?.img ||
          defaultHomepageConfig.pcBuilderSideBanners.left.img,
        href:
          parsed.pcBuilderSideBanners?.left?.href ||
          defaultHomepageConfig.pcBuilderSideBanners.left.href,
        alt:
          parsed.pcBuilderSideBanners?.left?.alt ||
          defaultHomepageConfig.pcBuilderSideBanners.left.alt,
        enabled: parsed.pcBuilderSideBanners?.left?.enabled !== false,
        verticalAlign:
          parsed.pcBuilderSideBanners?.left?.verticalAlign === 'top' ||
          parsed.pcBuilderSideBanners?.left?.verticalAlign === 'bottom'
            ? parsed.pcBuilderSideBanners.left.verticalAlign
            : 'center',
        offsetX: toFiniteNumber(parsed.pcBuilderSideBanners?.left?.offsetX, 0),
        offsetY: toFiniteNumber(parsed.pcBuilderSideBanners?.left?.offsetY, 0),
        imagePosX: toFiniteNumber(parsed.pcBuilderSideBanners?.left?.imagePosX, 50),
        imagePosY: toFiniteNumber(parsed.pcBuilderSideBanners?.left?.imagePosY, 50),
      },
      right: {
        img:
          parsed.pcBuilderSideBanners?.right?.img ||
          defaultHomepageConfig.pcBuilderSideBanners.right.img,
        href:
          parsed.pcBuilderSideBanners?.right?.href ||
          defaultHomepageConfig.pcBuilderSideBanners.right.href,
        alt:
          parsed.pcBuilderSideBanners?.right?.alt ||
          defaultHomepageConfig.pcBuilderSideBanners.right.alt,
        enabled: parsed.pcBuilderSideBanners?.right?.enabled !== false,
        verticalAlign:
          parsed.pcBuilderSideBanners?.right?.verticalAlign === 'top' ||
          parsed.pcBuilderSideBanners?.right?.verticalAlign === 'bottom'
            ? parsed.pcBuilderSideBanners.right.verticalAlign
            : 'center',
        offsetX: toFiniteNumber(parsed.pcBuilderSideBanners?.right?.offsetX, 0),
        offsetY: toFiniteNumber(parsed.pcBuilderSideBanners?.right?.offsetY, 0),
        imagePosX: toFiniteNumber(parsed.pcBuilderSideBanners?.right?.imagePosX, 50),
        imagePosY: toFiniteNumber(parsed.pcBuilderSideBanners?.right?.imagePosY, 50),
      },
    },
    footerSection: {
      groups: normalizeFooterSupportLinks(
        Array.isArray(parsed.footerSection?.groups)
          ? parsed.footerSection.groups
          : defaultHomepageConfig.footerSection.groups
      ),
      socials: Array.isArray(parsed.footerSection?.socials)
        ? normalizeFooterSocialLinks(parsed.footerSection.socials)
        : normalizeFooterSocialLinks(defaultHomepageConfig.footerSection.socials),
      bottomText:
        parsed.footerSection?.bottomText || defaultHomepageConfig.footerSection.bottomText,
    },
  };
}

export async function getHomepageConfig(
  queryFn: <T extends RowDataPacket[]>(sql: string, params?: unknown[]) => Promise<T>
) {
  const rows = await queryFn<RowDataPacket[]>(
    'SELECT setting_value FROM system_settings WHERE setting_key = ?',
    ['homepage_config']
  );
  const rawValue = rows[0]?.setting_value;
  return normalizeHomepageConfig(rawValue);
}
